// SettingsProfilePhotoPane.jsx

import React, { useRef, useState } from 'react';

// Services
import {
    clearUserProfileCover,
    uploadUserProfileCover,
} from 'services/users/UserProfileCoverService';

// Components
import Button from 'components/button/Button';
import ProfilePhoto from 'components/profilephoto/ProfilePhoto';

/**
 * SettingsProfilePhotoPane
 *
 * Renders upload and remove controls for the current user's profile photo.
 *
 * @param {object} theme - Active theme tokens for dynamic styling.
 * @param {object} user - Current signed-in USER node.
 *
 * @returns {ReactNode} The rendered profile photo settings pane.
 */
const SettingsProfilePhotoPane = React.memo(({
    theme,
    user,
}) => {

    // ----- STATE ----- //

    // File Input Reference
    const fileInputRef = useRef(null);

    // Upload Pending State
    const [isUploading, setIsUploading] = useState(false);

    // Remove Pending State
    const [isRemoving, setIsRemoving] = useState(false);

    // Error Message State
    const [errorMessage, setErrorMessage] = useState('');

    // Status Message State
    const [statusMessage, setStatusMessage] = useState('');

    // ----- MEMOS ----- //

    // Indicates whether profile photo editing is available.
    const canEditProfilePhoto = Boolean(user?.key);

    // Indicates whether a persisted profile photo exists.
    const hasProfilePhoto = typeof user?.coverUrl === 'string' && user.coverUrl.trim().length > 0;

    // ----- HANDLERS ----- //

    /**
     * Opens the hidden file picker when editing is allowed.
     */
    const handleChooseClick = () => {
        if (!canEditProfilePhoto || isUploading || isRemoving) return;
        fileInputRef.current?.click();
    };

    /**
     * Uploads the selected profile photo.
     *
     * @param {object} event - Input change event.
     */
    const handleFileChange = async (event) => {
        const file = event.target?.files?.[0] || null;
        event.target.value = '';

        if (!file || !user?.key) return;

        setErrorMessage('');
        setStatusMessage('Uploading profile photo...');
        setIsUploading(true);

        try {
            await uploadUserProfileCover({
                userNodeKey: user.key,
                file,
                actor: user,
                previousCoverPath: user?.coverStoragePath || user?.coverUrl || null,
            });
            setStatusMessage('Profile photo updated.');
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to upload profile photo.');
            setStatusMessage('');
        } finally {
            setIsUploading(false);
        }
    };

    /**
     * Clears the persisted profile photo fields.
     */
    const handleRemoveClick = async () => {
        if (!canEditProfilePhoto || !user?.key || !hasProfilePhoto || isUploading || isRemoving) return;

        setErrorMessage('');
        setStatusMessage('Removing profile photo...');
        setIsRemoving(true);

        try {
            await clearUserProfileCover({
                userNodeKey: user.key,
                actor: user,
                previousCoverPath: user?.coverStoragePath || user?.coverUrl || null,
            });
            setStatusMessage('Profile photo removed.');
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to remove profile photo.');
            setStatusMessage('');
        } finally {
            setIsRemoving(false);
        }
    };

    // ----- RENDER ----- //

    return (
        <>
            <div className="settings-modal-pane-header">
                <div className="settings-modal-pane-header-left">
                    <div className="settings-modal-pane-title" style={{ color: theme.foreground }}>
                        Profile Photo
                    </div>
                    <div className="settings-modal-pane-subtitle" style={{ color: theme.foregroundMuted }}>
                        Your profile photo used across workspaces, lists, and comments.
                    </div>
                </div>
            </div>

            <div className="settings-modal-pane-contents">
                <div className="settings-modal-cover-card" style={{ borderColor: theme.backgroundHover }}>
                    <div className="settings-modal-cover-preview">
                        <ProfilePhoto
                            node={user}
                            borderWidth={0}
                            size={80}
                        />
                    </div>

                    <div className="settings-modal-cover-meta">
                        <div className="settings-modal-setting-label" style={{ color: theme.foreground }}>
                            Profile photo
                        </div>
                        <div className="settings-modal-setting-description" style={{ color: theme.foregroundMuted }}>
                            Images are center-cropped to a square and optimized for 24x24 to 80x80 display.
                        </div>
                    </div>

                    <div className="settings-modal-cover-actions">
                        <Button
                            iconKey={hasProfilePhoto ? 'image' : 'upload'}
                            iconSize={16}
                            label={hasProfilePhoto ? 'Replace' : 'Upload'}
                            onClick={handleChooseClick}
                            enabled={canEditProfilePhoto && !isUploading && !isRemoving}
                        />

                        <Button
                            iconKey="delete"
                            iconSize={16}
                            label="Remove"
                            onClick={handleRemoveClick}
                            enabled={canEditProfilePhoto && hasProfilePhoto && !isUploading && !isRemoving}
                        />
                    </div>
                </div>

                {!canEditProfilePhoto && (
                    <div className="settings-modal-note" style={{ color: theme.foregroundMuted }}>
                        Sign in to update your profile photo.
                    </div>
                )}

                {(isUploading || isRemoving || statusMessage) && !errorMessage && (
                    <div className="settings-modal-status">{statusMessage}</div>
                )}

                {errorMessage && (
                    <div className="settings-modal-error">{errorMessage}</div>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
            </div>
        </>
    );
});

// Dev Tools
SettingsProfilePhotoPane.displayName = 'SettingsProfilePhotoPane';

export default SettingsProfilePhotoPane;